/* ---------------------------------------------------------- */
/*  src/hooks/useProductColumns.tsx                           */
/* ---------------------------------------------------------- */
import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import type { ColumnDef, Row } from '@tanstack/react-table';
import {
  ArrowUpDown,
  ImageIcon,
  PlusIcon,
  CheckIcon,
  XIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  type LucideIcon,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import type { OnChangeValue } from 'react-select';

import { useFamilies } from '@/api/familyApi';
import { normalizeFamily } from '@/utils/familyNormalizer';
import { FamilyDisplay } from '@/components/products/FamilyDisplay';

import { Product, productService } from '@/services/productService';
import { updateProductCategory } from '@/services/categoryService';
import { Category } from '@/types/categories';
import { CategoryTreeSelect } from '@/components/categories/CategoryTreeSelect';
import CreatableSelect from 'react-select/creatable';
import '@/styles/editable-cell.scss';
import { pickPrimaryImage } from '@/utils/images';
import { formatCurrency } from '@/lib/utils';
import { formatNumberForDisplay, formatDateForDisplay, DateFormat } from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useProductstableTranslations } from '@/hooks/useProductstableTranslations';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { logError, logDebug } from '@/utils/errorHandler';

/* ---------------------------------------------------------- */
/*  Helper / shared types                                     */
/* ---------------------------------------------------------- */
export interface CategoryOption {
  label: string;
  value: string | number;
}

export interface TagOption {
  label: string;
  value: string;
}

/* What the parent component passes down */
export interface UseProductColumnsOpts {
  /* --- table‐level state --- */
  editingCell: { rowIndex: number; columnId: string } | null;
  editValue: string | any; // Allow any to accommodate different types for tags
  tagOptions: TagOption[];
  categoryOptions: CategoryOption[];
  categoryTree: Category[]; // Full hierarchical category tree for breadcrumbs

  /* --- setters that columns mutate --- */
  setEditValue(v: string | any): void; // Allow any to accommodate different types for tags
  setCategoryOptions(updater: (prev: CategoryOption[]) => CategoryOption[]): void;
  setProducts?(updater: (prev: Product[]) => Product[] | Product[]): void;

  /* --- event/callback helpers from ProductsTable --- */
  handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void;
  handlePriceCellChange(e: React.ChangeEvent<HTMLInputElement>): void;
  handleSaveCellEdit(): void;
  handleCancelEdit(): void;
  handleCellEdit(row: number, col: string, v: string): void;
  handleCreateTagOption(input: string): Promise<TagOption | null>;
  updateData(row: number, col: string, v: any): Promise<void>;

  /* --- navigation / actions --- */
  handleRowClick(id: number): void;
  handleDelete(product: Product): void;

  /* --- misc helpers --- */
  toast(msg: { title: string; description?: string; variant?: string }): void;
  fetchData?(): void;

  /* tiny reusable icon button component */
  IconBtn: React.ComponentType<{
    tooltip: string;
    icon: LucideIcon;
    onClick(): void;
  }>;
}

/* ---------------------------------------------------------- */
/*  The Hook                                                  */
/* ---------------------------------------------------------- */
export function useProductColumns({
  /* destructure all opts */
  editingCell,
  editValue,
  tagOptions,
  categoryOptions: _categoryOptions,
  categoryTree,
  setEditValue,
  setCategoryOptions: _setCategoryOptions,
  setProducts: _setProducts,
  handleKeyDown,
  handlePriceCellChange: _handlePriceCellChange,
  handleSaveCellEdit,
  handleCancelEdit,
  handleCellEdit,
  handleCreateTagOption,
  updateData,
  handleRowClick,
  handleDelete,
  toast,
  fetchData: _fetchData,
  IconBtn,
}: UseProductColumnsOpts) {
  // i18n Step 1: Initialize translation hook
  const i18n = useProductstableTranslations();

  // i18n Step 1: Create stable memoized translations to prevent re-renders
  const translations = useMemo(
    () => ({
      // Column headers
      headers: {
        select: i18n.getColumnHeaderText('select'),
        image: i18n.getColumnHeaderText('image'),
        sku: i18n.getColumnHeaderText('sku'),
        name: i18n.getColumnHeaderText('name'),
        family: i18n.getColumnHeaderText('family'),
        category: i18n.getColumnHeaderText('category'),
        brand: i18n.getColumnHeaderText('brand'),
        tags: i18n.getColumnHeaderText('tags'),
        gtin: i18n.getColumnHeaderText('gtin'),
        price: i18n.getColumnHeaderText('price'),
        status: i18n.getColumnHeaderText('status'),
        created: i18n.getColumnHeaderText('created'),
        lastModify: i18n.getColumnHeaderText('lastModify'),
        actions: i18n.getColumnHeaderText('actions'),
      },
      // Placeholders and labels
      placeholders: {
        uncategorized: i18n.getColumnPlaceholderText('uncategorized'),
        noTags: i18n.getColumnPlaceholderText('noTags'),
      },
      tooltips: {
        selectAll: i18n.getColumnTooltipText('selectAll'),
        selectRow: i18n.getColumnTooltipText('selectRow'),
        saveChanges: i18n.getColumnTooltipText('saveChanges'),
        cancelEditing: i18n.getColumnTooltipText('cancelEditing'),
        view: i18n.getColumnTooltipText('view'),
        edit: i18n.getColumnTooltipText('edit'),
        archive: i18n.getColumnTooltipText('archive'),
      },
      labels: {
        active: i18n.getColumnLabelText('active'),
        inactive: i18n.getColumnLabelText('inactive'),
      },
      messages: {
        categoryUpdatedSuccessfully: i18n.getMessageText('categoryUpdatedSuccessfully'),
        failedToUpdateCategory: i18n.getMessageText('failedToUpdateCategory'),
        productActivated: i18n.getMessageText('productActivated'),
        productDeactivated: i18n.getMessageText('productDeactivated'),
        failedToUpdateStatus: i18n.getMessageText('failedToUpdateStatus'),
      },
    }),
    [i18n]
  );

  // ✅ Move useFamilies hook to top level to prevent infinite re-renders
  const { data: familiesData, isLoading: isFamiliesLoading } = useFamilies();
  const families = useMemo(() => familiesData || [], [familiesData]);

  // Use React Query client for cache invalidation
  const queryClient = useQueryClient();

  // Refs to store current stable values to avoid dependencies
  const toastRef = useRef(toast);
  const queryClientRef = useRef(queryClient);
  const handleCancelEditRef = useRef(handleCancelEdit);
  const handleSaveCellEditRef = useRef(handleSaveCellEdit);
  const categoryTreeRef = useRef(categoryTree);
  const updateDataRef = useRef(updateData);

  // Update refs whenever values change
  useEffect(() => {
    toastRef.current = toast;
    queryClientRef.current = queryClient;
    handleCancelEditRef.current = handleCancelEdit;
    handleSaveCellEditRef.current = handleSaveCellEdit;
    categoryTreeRef.current = categoryTree;
    updateDataRef.current = updateData;
  });

  // Get organization settings for formatting preferences and default price type
  const { orgSettings, defaultPriceType: orgDefaultPriceType } = useOrgSettings({
    includePricing: true,
  });
  const numberFormat = orgSettings?.number_format || 'dot';
  const dateFormat = (orgSettings?.date_format || 'MDY') as DateFormat;

  // Helper function to format currency respecting organization number format
  const formatCurrencyWithOrgFormat = useCallback(
    (amount: number | string | null | undefined, currency: string) => {
      // Convert to number and handle edge cases
      let numericAmount: number;

      if (typeof amount === 'number') {
        numericAmount = amount;
      } else if (typeof amount === 'string') {
        numericAmount = parseFloat(amount);
      } else {
        numericAmount = 0;
      }

      // Handle invalid numbers
      if (isNaN(numericAmount)) {
        numericAmount = 0;
      }

      if (numberFormat === 'comma') {
        // For comma format, use the formatNumberForDisplay and add currency symbol
        const formattedNumber = formatNumberForDisplay(numericAmount, 'comma', 2);
        return `${formattedNumber} ${currency}`;
      } else {
        // Use the existing formatCurrency for dot format
        return formatCurrency(numericAmount, currency);
      }
    },
    [numberFormat]
  );

  // Helper function to format dates with organization format
  const formatDateWithOrgFormat = useCallback(
    (dateString: string) => {
      if (!dateString) return '—';
      const date = new Date(dateString);
      return formatDateForDisplay(date, dateFormat);
    },
    [dateFormat]
  );

  // Helper function to select primary price based on organization's default price type
  const selectPrimaryPrice = useCallback(
    (prices: any[]) => {
      if (!prices || prices.length === 0) return null;

      // First try to find price matching organization's default price type (case-insensitive)
      if (orgDefaultPriceType) {
        const defaultSlug = (orgDefaultPriceType.code ?? '').toLowerCase();
        const defaultDisplay = (orgDefaultPriceType.label ?? '').toLowerCase();
        const defaultId = (orgDefaultPriceType.id ?? '').toString();

        const defaultPrice = prices.find(price => {
          const slug = (price.price_type ?? '').toString().toLowerCase();
          const display = (price.price_type_display ?? '').toString().toLowerCase();
          return slug === defaultSlug || slug === defaultId || display === defaultDisplay;
        });

        if (defaultPrice) return defaultPrice;
      }

      // Fallback to first available price
      return prices[0];
    },
    [orgDefaultPriceType]
  );

  // Helper function to safely update a product in the React Query cache
  const updateProductInCache = useCallback(
    (productId: number, updateFn: (product: Product) => Product) => {
      // Update ALL products queries (regardless of filter params) using predicate
      queryClient.setQueriesData({ queryKey: ['products'], exact: false }, (oldData: any) => {
        if (!oldData) return oldData;

        // Handle direct array response
        if (Array.isArray(oldData)) {
          return oldData.map((product: Product) =>
            product.id === productId ? updateFn(product) : product
          );
        }

        // Handle paginated response {results: [...], count: X}
        if (oldData.results && Array.isArray(oldData.results)) {
          return {
            ...oldData,
            results: oldData.results.map((product: Product) =>
              product.id === productId ? updateFn(product) : product
            ),
          };
        }

        return oldData;
      });
    },
    [queryClient]
  );

  // Create stable cancel handler - return the function directly, not a factory
  const stableCancelHandler = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent?.preventDefault?.();
    e.nativeEvent?.stopPropagation?.();
    handleCancelEditRef.current();
    return false;
  }, []); // ✅ NO DEPENDENCIES

  // Create handlers for IconBtn (no event parameter)
  const handleIconSave = useCallback(() => {
    handleSaveCellEditRef.current();
  }, []); // ✅ NO DEPENDENCIES

  // ✅ STABLE: Category change handler using refs
  const handleCategoryChange = useCallback(
    async (productId: number, newCategoryId: number | null) => {
      try {
        // Find the category breadcrumb path for the optimistic update
        let newCategoryName: string | null = null;
        if (newCategoryId) {
          const findCategoryBreadcrumb = (
            categories: Category[],
            id: number,
            path: string[] = []
          ): string | null => {
            for (const category of categories) {
              const currentPath = [...path, category.name];
              if (category.id === id) {
                return currentPath.join(' › ');
              }
              if (category.children) {
                const found = findCategoryBreadcrumb(category.children, id, currentPath);
                if (found) return found;
              }
            }
            return null;
          };
          newCategoryName = findCategoryBreadcrumb(categoryTree, newCategoryId);
        }

        // 1. Optimistic update using ref to avoid dependencies
        queryClientRef.current.setQueriesData(
          { queryKey: ['products'], exact: false },
          (oldData: any) => {
            if (!oldData) return oldData;

            // Handle direct array response
            if (Array.isArray(oldData)) {
              return oldData.map((product: Product) =>
                product.id === productId
                  ? {
                      ...product,
                      category_id: newCategoryId,
                      category_name: newCategoryName || undefined,
                    }
                  : product
              );
            }

            // Handle paginated response {results: [...], count: X}
            if (oldData.results && Array.isArray(oldData.results)) {
              return {
                ...oldData,
                results: oldData.results.map((product: Product) =>
                  product.id === productId
                    ? {
                        ...product,
                        category_id: newCategoryId,
                        category_name: newCategoryName || undefined,
                      }
                    : product
                ),
              };
            }

            return oldData;
          }
        );

        // 2. API call in background
        await updateProductCategory(productId, newCategoryId);

        // 3. Show success notification (no cache invalidation needed - optimistic update is sufficient)
        toastRef.current({
          title: translations.messages.categoryUpdatedSuccessfully,
          variant: 'default',
        });
      } catch (error) {
        logError(
          'Failed to update product category',
          {
            component: 'useProductColumns',
            action: 'handleCategoryChange',
            metadata: { productId, newCategoryId, error },
          },
          { showToast: false, userFacing: false }
        );
        toastRef.current({
          title: translations.messages.failedToUpdateCategory,
          variant: 'destructive',
        });

        // ⚠️ Only invalidate on error to revert
        queryClientRef.current.invalidateQueries({ queryKey: ['products'], exact: false });
      }
    },
    [translations.messages, categoryTree]
  );

  // ✅ STABLE: Category change handler factory
  const createCategoryChangeHandler = useCallback(
    (productId: number) => {
      return (nodeId: string | number) => {
        // Convert nodeId to number | null to match our handler
        let categoryId: number | null = null;
        if (nodeId !== null && nodeId !== undefined) {
          if (typeof nodeId === 'string') {
            const parsed = parseInt(nodeId, 10);
            categoryId = isNaN(parsed) ? null : parsed;
          } else {
            categoryId = nodeId;
          }
        }
        return handleCategoryChange(productId, categoryId);
      };
    },
    [handleCategoryChange]
  );

  /* -------- 1. main data columns -------------------------- */
  const columns = useMemo<ColumnDef<Product>[]>(() => {
    return [
      /**********  SELECT checkbox **************************************/
      {
        id: 'select',
        size: 20,
        header: ({ table }) => (
          <div className="px-1">
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() ? 'indeterminate' : false)
              }
              onCheckedChange={v => table.toggleAllPageRowsSelected(!!v)}
              aria-label={translations.tooltips.selectAll}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="px-0 text-center" onClick={e => e.stopPropagation()}>
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={v => row.toggleSelected(!!v)}
              aria-label={translations.tooltips.selectRow}
            />
          </div>
        ),
        enableSorting: false,
        enableHiding: false,
      },

      /**********  THUMBNAIL ********************************************/
      {
        accessorKey: 'thumbnail',
        size: 70,
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="p-0 hover:bg-transparent"
          >
            <span>{translations.headers.image}</span>
            <ArrowUpDown className="ml-1 size-4 opacity-70" />
          </Button>
        ),
        cell: ({ row }) => {
          // For table context, use pickPrimaryImage with thumbnail size for optimal performance
          const thumbnailUrl = pickPrimaryImage(row.original, 'thumbnail');
          const mediumUrl = pickPrimaryImage(row.original, 'medium');

          return (
            <div className="flex items-center space-x-2">
              {thumbnailUrl ? (
                <HoverCard>
                  <HoverCardTrigger>
                    <img
                      src={thumbnailUrl}
                      alt={row.original.name}
                      className="size-14 rounded border object-cover"
                      loading="lazy"
                      onError={e => {
                        // Fallback to medium if thumbnail fails
                        if (mediumUrl && e.currentTarget.src !== mediumUrl) {
                          e.currentTarget.src = mediumUrl;
                        }
                      }}
                    />
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80">
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold">{row.original.name}</h4>
                      <img
                        src={mediumUrl || thumbnailUrl}
                        alt={row.original.name}
                        className="h-48 w-full rounded object-cover"
                        loading="lazy"
                      />
                      <p className="text-xs text-muted-foreground">SKU: {row.original.sku}</p>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              ) : (
                <div className="flex size-14 items-center justify-center rounded border bg-muted">
                  <ImageIcon className="size-6 text-muted-foreground" />
                </div>
              )}
            </div>
          );
        },
      },

      /**********  SKU ***************************************************/
      {
        accessorKey: 'sku',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="w-full p-0 text-center hover:bg-transparent"
          >
            <span className="block w-full text-center">{translations.headers.sku}</span>
            <ArrowUpDown className="ml-1 size-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const rowIdx = row.index;
          const value = (row.getValue('sku') as string) ?? '';
          const isEditing = editingCell?.rowIndex === rowIdx && editingCell.columnId === 'sku';

          return isEditing ? (
            <div
              onClick={e => e.stopPropagation()}
              className="relative z-50 flex w-full space-x-2 p-1"
              data-editing="true"
            >
              <Input
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-8 w-full min-w-[120px]"
              />
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={() => {
                  logDebug('🏷️ Save button clicked', {
                    editValue,
                    editingCell,
                  });
                  handleIconSave();
                }}
                className="border-green-200 bg-green-50 text-green-700 shadow-sm transition-all duration-200 hover:scale-105 hover:border-green-300 hover:bg-green-100 hover:text-green-800 hover:shadow-md active:scale-95 active:bg-green-200"
                title={translations.tooltips.saveChanges}
              >
                <CheckIcon className="size-4" />
              </Button>
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={stableCancelHandler}
                className="border-red-200 bg-red-50 text-red-700 shadow-sm transition-all duration-200 hover:scale-105 hover:border-red-300 hover:bg-red-100 hover:text-red-800 hover:shadow-md active:scale-95 active:bg-red-200"
                title={translations.tooltips.cancelEditing}
              >
                <XIcon className="size-4" />
              </Button>
            </div>
          ) : (
            <div
              className="cursor-pointer rounded p-2 transition-colors hover:bg-accent/60 dark:hover:bg-accent/40"
              onClick={e => {
                e.stopPropagation();
                handleCellEdit(rowIdx, 'sku', value);
              }}
              data-editable="true"
            >
              {(() => {
                const str = String(value);
                return str.length > 15 ? `${str.slice(0, 15)}…` : str;
              })()}
            </div>
          );
        },
        enableSorting: true,
      },

      /**********  NAME **************************************************/
      {
        accessorKey: 'name',
        size: 260,
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="w-full p-0 text-center hover:bg-transparent"
          >
            <span className="block w-full text-center">{translations.headers.name}</span>
            <ArrowUpDown className="ml-1 size-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const rowIdx = row.index;
          const value = (row.getValue('name') as string) ?? '';
          const isEditing = editingCell?.rowIndex === rowIdx && editingCell.columnId === 'name';

          return isEditing ? (
            <div className="flex w-full space-x-2 p-1" data-editing="true">
              <Input
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-8 w-full"
              />
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={() => {
                  logDebug('🏷️ Save button clicked', {
                    editValue,
                    editingCell,
                  });
                  handleIconSave();
                }}
                className="border-green-200 bg-green-50 text-green-700 shadow-sm transition-all duration-200 hover:scale-105 hover:border-green-300 hover:bg-green-100 hover:text-green-800 hover:shadow-md active:scale-95 active:bg-green-200"
                title={translations.tooltips.saveChanges}
              >
                <CheckIcon className="size-4" />
              </Button>
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={stableCancelHandler}
                className="border-red-200 bg-red-50 text-red-700 shadow-sm transition-all duration-200 hover:scale-105 hover:border-red-300 hover:bg-red-100 hover:text-red-800 hover:shadow-md active:scale-95 active:bg-red-200"
                title={translations.tooltips.cancelEditing}
              >
                <XIcon className="size-4" />
              </Button>
            </div>
          ) : (
            <div
              className="cursor-pointer rounded p-2 transition-colors hover:bg-accent/60 dark:hover:bg-accent/40"
              onClick={e => {
                e.stopPropagation();
                handleCellEdit(rowIdx, 'name', value);
              }}
              data-editable
            >
              {String(value)}
            </div>
          );
        },
        enableSorting: true,
      },

      /**********  FAMILY **************************************************/
      {
        accessorKey: 'family',
        size: 200,
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="w-full p-0 text-center hover:bg-transparent"
            >
              <span className="block w-full text-center">{translations.headers.family}</span>
              <ArrowUpDown className="ml-1 size-4" />
            </Button>
          );
        },
        cell: ({ row }) => {
          // Get family data from the product - might be a family object, ID, or family_name string
          const family = row.original.family;
          const familyName = row.original.family_name;

          // First try using family object or ID if available
          if (family) {
            // First, normalize the family data for consistent format
            let normalizedFamily = normalizeFamily(family);

            // If we have families data and normalized family shows a generic label ("Family X"),
            // try to find the complete family data from the families list
            if (normalizedFamily && families.length > 0 && !isFamiliesLoading) {
              // Check if we need to enhance with full family data (when label is generic "Family X" or is missing properties)
              const hasGenericLabel = normalizedFamily.label.includes(
                `Family ${normalizedFamily.id}`
              );
              const isMissingProperties =
                !normalizedFamily.code || normalizedFamily.code === `family-${normalizedFamily.id}`;

              if (hasGenericLabel || isMissingProperties) {
                const fullFamily = families.find(f => f.id === normalizedFamily?.id);
                if (fullFamily) {
                  normalizedFamily = normalizeFamily(fullFamily);
                }
              }
            }

            return (
              <div className="flex w-full justify-center p-1">
                <FamilyDisplay
                  family={normalizedFamily}
                  badgeVariant="secondary"
                  showEmpty={true}
                  showCode={false}
                  hideTooltip={true}
                  className="w-full bg-emerald-100 px-2 text-center text-emerald-800 transition-colors hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
                />
              </div>
            );
          }

          // If direct family object isn't available but family_name is, use that
          if (familyName) {
            return (
              <div className="flex w-full justify-center p-1">
                <FamilyDisplay
                  family={{ label: familyName }}
                  badgeVariant="secondary"
                  showEmpty={true}
                  showCode={false}
                  hideTooltip={true}
                  className="w-full bg-emerald-100 px-2 text-center text-emerald-800 transition-colors hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
                />
              </div>
            );
          }

          // No family data found
          return (
            <div className="flex w-full justify-center p-1">
              <FamilyDisplay
                family={null}
                badgeVariant="secondary"
                showEmpty={true}
                showCode={false}
                hideTooltip={true}
                className="w-full bg-emerald-100 px-2 text-center text-emerald-800 transition-colors hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
              />
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;

          // Try to match against family object first
          const family = row.getValue(columnId);
          // Then try to match against family_name
          const familyName = row.original.family_name;

          if (family) {
            // Handle different possible family data formats
            let familyId: number | null = null;

            if (typeof family === 'object' && family !== null) {
              // Family might be a complex object with an ID property
              familyId = (family as any).id;
            } else if (typeof family === 'number') {
              // Family might be just the ID
              familyId = family;
            }

            // Compare family ID with filter value
            return String(familyId) === String(filterValue);
          } else if (familyName) {
            // If using family_name, do a string match
            const familyNameLower = familyName.toLowerCase();
            const filterValueLower = String(filterValue).toLowerCase();
            return familyNameLower.includes(filterValueLower);
          }

          return false;
        },
      },

      /**********  CATEGORY **************************************************/
      {
        accessorKey: 'category_name',
        size: 190,
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="w-full p-0 text-center hover:bg-transparent"
            >
              <span className="block w-full text-center">{translations.headers.category}</span>
              <ArrowUpDown className="ml-1 size-4" />
            </Button>
          );
        },
        cell: ({ row }) => {
          const product = row.original;
          const rowIndex = row.index;

          // Get category name (already includes full breadcrumb from backend)
          const categoryDisplayText = product.category_name || '';

          if (!categoryDisplayText) {
            return (
              <div className="flex h-8 items-center justify-center text-xs text-muted-foreground">
                <CategoryTreeSelect
                  key={`category-select-${product.id}-${rowIndex}`}
                  selectedValue={product.category_id || null}
                  onChange={createCategoryChangeHandler(product.id || 0)}
                  placeholder={translations.placeholders.uncategorized}
                  displayText={translations.placeholders.uncategorized}
                  treeData={categoryTree}
                  className="h-auto w-full min-w-0 border-none bg-transparent shadow-none hover:bg-accent/50 focus:bg-accent"
                />
              </div>
            );
          }

          // Create multi-line display text for CategoryTreeSelect
          const breadcrumbParts = categoryDisplayText.split(' > ');
          const multiLineDisplayText = breadcrumbParts.join('\n');

          return (
            <div className="flex min-h-[60px] w-full items-center px-1 py-2">
              <CategoryTreeSelect
                key={`category-select-${product.id}-${rowIndex}`}
                selectedValue={product.category_id || null}
                onChange={createCategoryChangeHandler(product.id || 0)}
                placeholder={translations.placeholders.uncategorized}
                displayText={multiLineDisplayText}
                treeData={categoryTree}
                className="multi-line-breadcrumb h-auto w-full min-w-0 border-none bg-transparent text-xs shadow-none hover:bg-accent/50 focus:bg-accent"
              />
            </div>
          );
        },
        filterFn: 'equals',
      },

      /**********  BRAND **************************************************/
      {
        accessorKey: 'brand',
        size: 180,
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="w-full p-0 text-center hover:bg-transparent"
            >
              <span className="block w-full text-center">{translations.headers.brand}</span>
              {column.getIsSorted() === 'asc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : (
                <ArrowUpDown className="ml-1 size-4 opacity-30" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const rowIndex = row.index;
          const value = (row.getValue('brand') as string) || '';
          const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.columnId === 'brand';

          if (isEditing) {
            return (
              <div
                className="relative z-50 flex w-full space-x-2 p-1"
                onClick={e => e.stopPropagation()}
                data-editing="true"
              >
                <Input
                  className="h-8 w-full min-w-[120px]"
                  autoFocus
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    logDebug('🏷️ Save button clicked', {
                      editValue,
                      editingCell,
                    });
                    handleIconSave();
                  }}
                  className="border-green-200 bg-green-50 text-green-700 shadow-sm transition-all duration-200 hover:scale-105 hover:border-green-300 hover:bg-green-100 hover:text-green-800 hover:shadow-md active:scale-95 active:bg-green-200"
                  title={translations.tooltips.saveChanges}
                >
                  <CheckIcon className="size-4" />
                </Button>
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={stableCancelHandler}
                  className="border-red-200 bg-red-50 text-red-700 shadow-sm transition-all duration-200 hover:scale-105 hover:border-red-300 hover:bg-red-100 hover:text-red-800 hover:shadow-md active:scale-95 active:bg-red-200"
                  title={translations.tooltips.cancelEditing}
                >
                  <XIcon className="size-4" />
                </Button>
              </div>
            );
          }

          // Non-editing brand display - simple text only, no icons
          return (
            <div
              className="w-full cursor-pointer p-2 text-center text-sm font-medium transition-colors hover:text-primary"
              title={value || '—'}
              onClick={e => {
                e.stopPropagation();
                handleCellEdit(rowIndex, 'brand', value);
              }}
              data-editable="true"
            >
              <span className="border-b border-dotted border-gray-400 pb-0.5">{value || '—'}</span>
            </div>
          );
        },
        enableSorting: true,
      },
      /**********  TAGS **************************************************/
      {
        accessorKey: 'tags',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="w-full p-0 text-center hover:bg-transparent"
            >
              <span className="block w-full text-center">{translations.headers.tags}</span>
              {column.getIsSorted() === 'asc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : (
                <ArrowUpDown className="ml-1 size-4 opacity-30" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const rowIndex = row.index;
          // Handle tags which could be a string or array
          let tags: string[] = [];
          const rawTags = row.getValue('tags');

          // Safely parse tags from various formats
          if (Array.isArray(rawTags)) {
            tags = rawTags;
          } else if (typeof rawTags === 'string') {
            try {
              // Try to parse if it's a JSON string
              const parsed = JSON.parse(rawTags);
              tags = Array.isArray(parsed) ? parsed : [rawTags];
            } catch {
              // If not valid JSON, treat as a single tag
              tags = [rawTags];
            }
          }

          const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.columnId === 'tags';
          // Map editValue to tag option objects for the select's value
          let editTagOptions: TagOption[] = [];

          if (isEditing) {
            // Handle editValue which could be a JSON string or array
            let parsedTags: string[] = [];

            // If editValue is null/undefined, use the current tags as initial value
            const valueToProcess = editValue !== null && editValue !== undefined ? editValue : tags;

            if (valueToProcess) {
              if (typeof valueToProcess === 'string') {
                try {
                  parsedTags = JSON.parse(valueToProcess);
                } catch {
                  parsedTags = [valueToProcess];
                }
              } else if (Array.isArray(valueToProcess)) {
                parsedTags = valueToProcess;
              }
            }

            editTagOptions = parsedTags.map(tag => {
              // Try to find the exact option object from tagOptions first
              const existingOption = tagOptions.find(opt => opt.value === tag || opt.label === tag);
              if (existingOption) {
                return existingOption; // Use the exact same object reference
              }
              // Only create new object if tag doesn't exist in tagOptions
              return { label: String(tag), value: String(tag) };
            });

            // Debug logging
            logDebug('🏷️ Tags edit state', {
              rowIndex,
              originalTags: tags,
              editValue,
              valueToProcess,
              parsedTags,
              editTagOptions,
              tagOptionsLength: tagOptions.length,
            });
          }

          if (isEditing) {
            return (
              <div className="min-w-[240px] rounded p-2" data-editing="true">
                <CreatableSelect
                  isMulti
                  value={editTagOptions}
                  options={tagOptions}
                  closeMenuOnSelect={false}
                  blurInputOnSelect={false}
                  onChange={(options: OnChangeValue<TagOption, true>) => {
                    // Convert selected options to array of tag strings
                    const values = Array.isArray(options)
                      ? options.map(opt => String(opt.value))
                      : [];
                    logDebug('🏷️ Tags onChange triggered', {
                      options,
                      values,
                      currentEditValue: editValue,
                      willSetEditValue: values,
                    });
                    setEditValue(values);
                    logDebug('🏷️ setEditValue called with', values);
                  }}
                  onCreateOption={async (inputValue: string) => {
                    logDebug('🏷️ Creating new tag', inputValue);
                    // Call handleCreateTagOption which returns TagOption or null
                    const newOption = await handleCreateTagOption(inputValue);

                    // Only proceed if we got a valid tag option back
                    if (newOption && newOption.value) {
                      logDebug('🏷️ New tag created', newOption);
                      // Get current values as array - use the processed value
                      const currentValues = Array.isArray(editValue) ? editValue : tags || [];

                      // Update with new value
                      const newValues = [...currentValues, String(newOption.value)];
                      logDebug('🏷️ Setting new values after create', newValues);
                      setEditValue(newValues);
                    }
                  }}
                  onMenuOpen={() => logDebug('🏷️ Menu opened')}
                  onMenuClose={() => logDebug('🏷️ Menu closed')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder={i18n.getBulkTagModalText('placeholders.searchOrCreateTags')}
                  isClearable
                  hideSelectedOptions={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  captureMenuScroll={false}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    control: base => ({ ...base, minHeight: 38 }),
                    menu: base => ({ ...base, zIndex: 9999 }),
                  }}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? i18n.getBulkTagModalText('messages.noTagsFound', { searchTerm: inputValue })
                      : i18n.getBulkTagModalText('messages.typeToSearchTags')
                  }
                  formatCreateLabel={(inputValue: string) =>
                    i18n.getBulkTagModalText('messages.formatCreateLabel', { inputValue })
                  }
                />
                <div className="mt-2 flex justify-end space-x-2">
                  <Button
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={() => {
                      logDebug('🏷️ Save button clicked', {
                        editValue,
                        editingCell,
                      });
                      handleIconSave();
                    }}
                    className="border-green-200 bg-green-50 text-green-700 shadow-sm transition-all duration-200 hover:scale-105 hover:border-green-300 hover:bg-green-100 hover:text-green-800 hover:shadow-md active:scale-95 active:bg-green-200"
                    title={translations.tooltips.saveChanges}
                  >
                    <CheckIcon className="size-4" />
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={stableCancelHandler}
                    className="border-red-200 bg-red-50 text-red-700 shadow-sm transition-all duration-200 hover:scale-105 hover:border-red-300 hover:bg-red-100 hover:text-red-800 hover:shadow-md active:scale-95 active:bg-red-200"
                    title={translations.tooltips.cancelEditing}
                  >
                    <XIcon className="size-4" />
                  </Button>
                </div>
              </div>
            );
          }
          // Non-editing mode: show tags as badges and a single + button
          return (
            <div
              className="flex w-full flex-wrap items-center justify-start gap-1 overflow-hidden p-1"
              style={{ maxWidth: '200px' }}
            >
              {tags && tags.length > 0 ? (
                tags.slice(0, 3).map((tag, i) => (
                  <Badge key={i} variant="outline" className="whitespace-nowrap text-xs">
                    {tag}
                  </Badge>
                ))
              ) : (
                <span className="text-xs text-slate-400">{translations.placeholders.noTags}</span>
              )}
              {tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{tags.length - 3}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="ml-1 size-5 shrink-0"
                aria-label="Edit tags"
                onClick={e => {
                  e.stopPropagation();
                  // Pass the actual array of tags directly
                  handleCellEdit(rowIndex, 'tags', tags as any);
                }}
              >
                <PlusIcon className="size-3" />
              </Button>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row: Row<Product>, columnId: string, filterValue: any): boolean => {
          if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) return true;

          // Get tags and normalize them
          let tags: string[] = [];
          const rawTags = row.getValue(columnId);

          // Safely parse tags from various formats
          if (Array.isArray(rawTags)) {
            tags = rawTags;
          } else if (typeof rawTags === 'string') {
            try {
              // Try to parse if it's a JSON string
              const parsed = JSON.parse(rawTags);
              tags = Array.isArray(parsed) ? parsed : [rawTags];
            } catch {
              // If not valid JSON, treat as a single tag
              tags = [rawTags];
            }
          }

          if (!tags || tags.length === 0) return false;

          // Normalize tags for comparison (trim and lowercase)
          const normalizedProductTags = tags.map(tag => String(tag).trim().toLowerCase());
          const normalizedFilterTags = filterValue.map((tag: string) =>
            String(tag).trim().toLowerCase()
          );

          // AND logic: ALL filter tags must be present in this product's tags
          const hasAllTags = normalizedFilterTags.every(filterTag =>
            normalizedProductTags.includes(filterTag)
          );

          return hasAllTags;
        },
      },
      /**********  BARCODE **************************************************/
      {
        accessorKey: 'barcode',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="w-full p-0 text-center hover:bg-transparent"
            >
              <span className="block w-full text-center">{translations.headers.gtin}</span>
              {column.getIsSorted() === 'asc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : (
                <ArrowUpDown className="ml-1 size-4 opacity-30" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const rowIndex = row.index;
          const value = (row.getValue('barcode') as string) || '';
          const isEditing =
            editingCell?.rowIndex === rowIndex && editingCell?.columnId === 'barcode';

          if (isEditing) {
            return (
              <div
                className="relative z-50 flex w-full space-x-2 p-1"
                onClick={e => e.stopPropagation()}
                data-editing="true"
              >
                <Input
                  className="h-8 w-full min-w-[120px]"
                  autoFocus
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    logDebug('🏷️ Save button clicked', {
                      editValue,
                      editingCell,
                    });
                    handleIconSave();
                  }}
                  className="border-green-200 bg-green-50 text-green-700 shadow-sm transition-all duration-200 hover:scale-105 hover:border-green-300 hover:bg-green-100 hover:text-green-800 hover:shadow-md active:scale-95 active:bg-green-200"
                  title={translations.tooltips.saveChanges}
                >
                  <CheckIcon className="size-4" />
                </Button>
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={stableCancelHandler}
                  className="border-red-200 bg-red-50 text-red-700 shadow-sm transition-all duration-200 hover:scale-105 hover:border-red-300 hover:bg-red-100 hover:text-red-800 hover:shadow-md active:scale-95 active:bg-red-200"
                  title={translations.tooltips.cancelEditing}
                >
                  <XIcon className="size-4" />
                </Button>
              </div>
            );
          }

          return (
            <div
              className="cursor-pointer truncate p-1 font-mono text-sm transition-colors hover:text-primary"
              onClick={e => {
                e.stopPropagation();
                handleCellEdit(rowIndex, 'barcode', value);
              }}
              data-editable="true"
            >
              <span className="editable-cell">{value || '—'}</span>
            </div>
          );
        },
        enableSorting: true,
      },
      /**********  PRICE **************************************************/
      {
        accessorKey: 'price',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="w-full p-0 text-center hover:bg-transparent"
          >
            <span className="block w-full text-center">{translations.headers.price}</span>
            <ArrowUpDown className="ml-1 size-4 opacity-70" />
          </Button>
        ),
        cell: ({ row, column: _column, table: _table }) => {
          // Check for multiple prices
          const hasPrices = row.original.prices && row.original.prices.length > 0;
          const primaryPrice = hasPrices ? selectPrimaryPrice(row.original.prices || []) : null;

          return (
            <div
              className="cursor-pointer"
              onClick={e => {
                e.stopPropagation();
                // Navigate to product detail view instead of edit page
                const productId = row.original.id;
                if (productId) {
                  handleRowClick(productId);
                }
              }}
            >
              {hasPrices && primaryPrice ? (
                <div className="p-1">
                  {formatCurrencyWithOrgFormat(primaryPrice.amount, primaryPrice.currency)}
                  {row.original.prices && row.original.prices.length > 1 && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      +{row.original.prices.length - 1}
                    </span>
                  )}
                </div>
              ) : (
                <div className="p-1">
                  {formatCurrencyWithOrgFormat(
                    (row.original as any).price || 0,
                    (row.original as any).currency || 'USD'
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      /**********  STATUS **************************************************/
      {
        accessorKey: 'is_active',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="w-full p-0 text-center hover:bg-transparent"
            >
              <span className="block w-full text-center">{translations.headers.status}</span>
              {column.getIsSorted() === 'asc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : (
                <ArrowUpDown className="ml-1 size-4 opacity-30" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const isActive = row.getValue('is_active') as boolean;
          const productId = row.original.id;

          const handleToggleStatus = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (productId) {
              const newStatus = !isActive;

              // 1. Optimistic update in cache (immediate UI change)
              updateProductInCache(productId, product => ({
                ...product,
                is_active: newStatus,
              }));

              // 2. API call in background
              productService
                .updateProduct(productId, { is_active: newStatus })
                .then(() => {
                  toastRef.current({
                    title: newStatus
                      ? translations.messages.productActivated
                      : translations.messages.productDeactivated,
                    variant: 'default',
                  });
                  // ✅ No cache invalidation needed - already updated optimistically
                })
                .catch(_ => {
                  toastRef.current({
                    title: translations.messages.failedToUpdateStatus,
                    variant: 'destructive',
                  });
                  // ⚠️ Only invalidate on error to revert
                  queryClientRef.current.invalidateQueries({
                    queryKey: ['products'],
                    exact: false,
                  });
                });
            }
          };

          return (
            <div className="cursor-pointer p-1" onClick={handleToggleStatus} data-editable="true">
              <Badge
                variant={isActive ? 'default' : 'secondary'}
                className={`w-[80px] justify-center ${isActive ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              >
                {isActive ? translations.labels.active : translations.labels.inactive}
              </Badge>
            </div>
          );
        },
        enableSorting: true,
      },
      /**********  CREATED **************************************************/
      {
        accessorKey: 'created_at',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="w-full p-0 text-center hover:bg-transparent"
            >
              <span className="block w-full text-center">{translations.headers.created}</span>
              {column.getIsSorted() === 'asc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : (
                <ArrowUpDown className="ml-1 size-4 opacity-30" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const dateString = row.getValue('created_at') as string;

          if (!dateString) return '—';

          // Parse and format date
          const formattedDate = formatDateWithOrgFormat(dateString);

          return <div>{formattedDate}</div>;
        },
        sortingFn: 'datetime',
        enableSorting: true,
      },
      /**********  UPDATED **************************************************/
      {
        accessorKey: 'updated_at',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="w-full p-0 text-center hover:bg-transparent"
            >
              <span className="block w-full text-center">{translations.headers.lastModify}</span>
              {column.getIsSorted() === 'asc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowUpDown className="ml-1 size-4" />
              ) : (
                <ArrowUpDown className="ml-1 size-4 opacity-30" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const dateString = row.getValue('updated_at') as string;

          if (!dateString) return '—';

          // Parse and format date
          const formattedDate = formatDateWithOrgFormat(dateString);

          return <div>{formattedDate}</div>;
        },
        sortingFn: 'datetime',
        enableSorting: true,
      },
    ];
  }, [
    editingCell,
    editValue,
    tagOptions,
    categoryTree,
    families,
    isFamiliesLoading,
    translations,
    stableCancelHandler,
    createCategoryChangeHandler,
    formatCurrencyWithOrgFormat,
    formatDateWithOrgFormat,
    handleCellEdit,
    handleCreateTagOption,
    handleIconSave,
    handleKeyDown,
    handleRowClick,
    setEditValue,
    updateProductInCache,
  ]);

  /* -------- 2. sticky action column ----------------------- */
  const ACTION_W = 90;
  const actionColumn = useMemo<ColumnDef<Product>>(
    () => ({
      id: 'actions',
      size: ACTION_W,
      enableHiding: false,
      enableSorting: false,
      header: () => (
        <div className="sticky right-0 z-10 w-full min-w-[90px] bg-muted/50 py-1 text-center text-xs font-medium dark:bg-muted/30">
          <div className="border-l border-border/40 pl-2">{translations.headers.actions}</div>
        </div>
      ),
      cell: ({ row }) => {
        const id = row.original.id;
        if (!id) return null;
        return (
          <div className="sticky right-0 z-10 flex w-full min-w-[90px] justify-center bg-transparent">
            <div className="flex w-full justify-center border-l border-border/40 pl-2">
              <div className="flex flex-col items-center gap-1 rounded-lg bg-card p-1 shadow-sm">
                <IconBtn
                  tooltip={translations.tooltips.view}
                  icon={EyeIcon}
                  onClick={() => handleRowClick(id)}
                />
                <IconBtn
                  tooltip={translations.tooltips.edit}
                  icon={PencilIcon}
                  onClick={() => handleRowClick(id)}
                />
                <PermissionGuard permission="product.delete">
                  <IconBtn
                    tooltip={translations.tooltips.archive}
                    icon={TrashIcon}
                    onClick={() => handleDelete(row.original)}
                  />
                </PermissionGuard>
              </div>
            </div>
          </div>
        );
      },
    }),
    [
      translations.headers.actions,
      translations.tooltips.view,
      translations.tooltips.edit,
      translations.tooltips.archive,
      IconBtn,
      handleRowClick,
      handleDelete,
    ]
  );

  /* -------- 3. concat + return ---------------------------- */
  const allColumns = useMemo(() => [...columns, actionColumn], [columns, actionColumn]);

  return { columns, actionColumn, allColumns };
}

/* ---------------------------------------------------------- */
/*  END of hook                                               */
/* ---------------------------------------------------------- */
